<script setup>
import { ref, reactive, computed, watch, nextTick, onBeforeUpdate, onMounted, onUnmounted } from 'vue';
import { saveAs } from "file-saver";
import { DateTime } from "luxon";
import { useAuthStore } from "../../stores/auth";
import { useSocketStore } from "../../stores/socketStore";
import useSocketListeners from "../../composables/useSocketListeners.js";
import { useSessionStore } from "../../stores/sessionStore";
import { saveInteractionData } from "../../helpers/interactionHelper";
import { compactDate, numericDate, shortDate, timeOfDay } from "../../helpers/usDate";
import { draftFor, keepDraft } from "../../helpers/chatDrafts";
import CheckboxInput from "./shared/inputs/CheckboxInput.vue";
import Dropdown from "./shared/Dropdown.vue";
import GroupNameModal from "./shared/modals/GroupNameModal.vue";
import IconBack from "../icons/IconBack.vue";
import IconChatParticipants from "../icons/IconChatParticipants.vue";
import IconInfo from "../icons/IconInfo.vue";
import IconMessageOff from "../icons/IconMessageOff.vue";
import IconMessageOn from "../icons/IconMessageOn.vue";
import IconUserCreate from "../icons/IconUserCreate.vue";
import IconVideo from "../icons/IconVideo.vue";
import IconVideoOff from "../icons/IconVideoOff.vue";
import Loading from "./shared/loading/Loading.vue";
import ModalComponent from "./shared/modals/ModalComponent.vue";
import NotificationsCount from "./shared/notifications/NotificationsCount.vue";
import PgeButton from "../../pge-components/pge-button.vue";
import PgeEmojiPicker from "../../pge-components/pge-emoji-picker.vue";
import SearchInput from "./shared/inputs/SearchInput.vue";
import Tooltip from "./shared/tooltip/Tooltip.vue";
import EmojiPicker from 'vue3-emoji-picker';
import Session from "../../livekit-client/Session.vue";
import SessionLoadingSkeleton from "../../livekit-client/components/SessionLoadingSkeleton.vue";

let currentGroupId = null;

const props = defineProps([
  "template",
  "jitsi",
  "allUsers",
  "user",
  "view",
  "group",
  "allNotifications",
  "userShow",
  "closeGroup",
  "videoUrl",
  "isChatHidden",
  "leaveVideo",
  "selectedGroup",
  "inSession",
  "session",
  "jitsiLoaded",
  // Whether the session's "Enable In-Session Chat" toggle allows messaging.
  // Defaults true so hub-wide chats (which this toggle does not govern) and any
  // caller that omits it keep working unchanged.
  "inSessionChatEnabled",
  // Whether THIS user may use the session chats at all — false for a Member
  // once hub peer-to-peer messaging is off (Hub Staff / the Hub Owner stay
  // true). Resolved by OmniChat; `undefined` from any other caller means no
  // restriction.
  "sessionChatsVisible",
]);

const emit = defineEmits(['back', 'startVideo', 'joinVideo', 'leaveVideo', 'hide-chat', 'message-sent', 'view-update', 'attendee-left', 'rename-group', 'reactions-update']);

const authStore = useAuthStore();
const socketStore = useSocketStore();
const socketListeners = useSocketListeners();
const sessionStore = useSessionStore();

// ----- The session's chat on a phone (Mobile in-session → Chat; prototype
// sheetChat). It sits in a bottom sheet: "Message everyone", the host marked,
// a red "New" line where the unread start, and the newest message in view
// whenever the sheet opens. The desktop chat is as it was.
const phoneChat = computed(() => Boolean(props.inSession && sessionStore.phoneRoom));
const isHostMessage = (m) => {
  const host = props.session?.session_moderator?.sso_user_id;
  return host != null && m?.user?.user_id != null && String(m.user.user_id) === String(host);
};
const firstNewId = computed(() => {
  const unread = sessionStore.chatNewFrom;
  if (!phoneChat.value || !unread) return null;
  const all = Object.values(messages.value ?? {}).flat();
  const at = all.length - unread;
  return at >= 0 && at < all.length ? all[at]._id : null;
});
const hasMessages = computed(() => Object.values(messages.value ?? {}).some((list) => list.length));
const scrollToNewest = () => {
  const list = document.querySelector(`#group-${props.group?._id} ul`);
  if (list) list.scrollTop = list.scrollHeight;
};
// Opened: once the sheet is drawn, and again when its rise has finished.
watch(() => sessionStore.chatOpenTick, async () => {
  if (!phoneChat.value) return;
  await nextTick();
  scrollToNewest();
  setTimeout(scrollToNewest, 280);
});

const showGroupNameModal = ref(false);
const videoStart = ref(false);
const modal = reactive({
  groupUsers: false,
  groupRemove: false,
  groupChatParticipants: false,
  groupInviteOthers: false,
  groupLeaveChat: false,
});
const tooltip = reactive({
  visible: false,
  target: null,
  targetLeft: false,
  text: "",
  initial: "bottom",
  xPosition: "center"
});
const emojiTooltip = reactive({
  visible: false,
  target: null,
  targetLeft: false,
  text: "",
  initial: "top",
  xPosition: "right"
});
const reactionEmojiTooltip = reactive({
  visible: false,
  target: null,
  targetLeft: false,
  text: "",
  initial: "top",
  xPosition: "right"
});
const removeUserConfirmation = ref(false);
// A draft survives the component going away and coming back (helpers/chatDrafts).
const message = ref(draftFor(props.group?._id) || null);
watch(message, (text) => keepDraft(props.group?._id, text || ''));
const showActions = ref(false);
const selectedUsers = ref([]);
const users = ref([]);
const otherUsers = ref([]);
const filteredOtherUsers = ref([]);
const filteredUsers = ref([]);
const usersLoading = ref(false);
const textareaFocused = ref(false);
const currentGroup = ref(null);
const textareaEl = ref(null);
const hoveredMessageId = ref(null);
const reactingMessageId = ref(null);
const livekitConnected = ref(false);
const videoEvent = ref(null);
const sessionRef = ref(null);
const failedAvatars = reactive({});

const onAvatarError = (url) => {
  if (url) failedAvatars[url] = true;
};

const canRemove = computed(() => {
  return props.group && props.group.user_id === authStore.userData.id;
});

// Hide video call button for session groups when the session is still active with other participants
const isSessionGroupWithActiveSession = computed(() => {
  if (!props.group) return false;
  return props.group.isSessionGroup && sessionStore.activeSessionId !== null;
});

// This session chat is read-only: history stays visible, but nothing can be
// sent. Two causes, both session-group-only — one-to-one and regular group
// chats are never gated by either:
//   - the session's "Enable In-Session Chat" toggle is off (blocks everyone);
//   - hub peer-to-peer messaging is off and this user is a Member (Hub Staff /
//     the Hub Owner keep sending).
// `undefined` on either prop means the caller didn't pass it (hub-wide chat),
// which is not a restriction. The websocket rejects the send regardless.
const isSendingBlocked = computed(() => {
  if (!(props.group && props.group.isSessionGroup)) return false;
  return props.inSessionChatEnabled === false || props.sessionChatsVisible === false;
});

const messages = computed(() => {
  const user = authStore.userData;
  const { group } = props;
  let messages = [];

  const groupBy = (array, key) => {
    return array.reduce((result, currentItem) => {
      (result[currentItem[key]] = result[currentItem[key]] || []).push(currentItem);
      return result;
    }, {});
  };

  if (group.messages) {
    messages = group.messages.map((m) => {
      const resolved = Array.isArray(m.user) ? m.user[0] : m.user;
      m.user = resolved || { user_id: m.user_id, fullName: 'Unknown member', avatar: null };

      m.date = DateTime.fromISO(m.created_at).toISODate();
      m.you = m.user.user_id === user.id;
      if (m.user.avatar === 'null' || m.user.avatar === 'undefined' || m.user.avatar === '') {
        m.user.avatar = null;
      }
      return m;
    });
  }

  return groupBy(messages, "date");
});

const inVideoCall = computed(() => {
  if (props.group) {
    const { videoUsers } = props.group;
    return videoUsers.includes(authStore.userData.id);
  }
  return false;
});

watch(() => props.view, (newVal) => {
  if (newVal === "messages") {
    setTimeout(() => {
      if (textareaEl.value) {
        textareaEl.value.focus();
        textareaFocused.value = true;
      }
    }, 300);
  } else {
    videoStart.value = false;
    sessionStore.setIsVideoChat(false);
    message.value = null;
    showGroupNameModal.value = false;
    modal.groupUsers = false;
    modal.groupRemove = false;
    modal.groupChatParticipants = false;
    modal.groupInviteOthers = false;
    modal.groupUsersRemove = false;
  }
});

watch(() => props.group, (newVal) => {
  if (newVal) {
    currentGroup.value = newVal;
  }
});

onBeforeUpdate(() => {
  const { group } = props;

  if (group && currentGroupId !== group._id) showActions.value = false;
  if (group) currentGroupId = group._id;
});

const handleSessionReady = () => {
  // TODO
};

const handleSessionConnected = () => {
  livekitConnected.value = true;
};

const handleSessionDisconnected = async ({ participantId, remainingParticipants }) => {
  livekitConnected.value = false;
  videoStart.value = false;
  sessionStore.setIsVideoChat(false);
  videoEvent.value = null;
  sessionStore.reset();
  emit("leaveVideo", props.group);
};

const handleSessionClose = () => {
  livekitConnected.value = false;
  videoStart.value = false;
  sessionStore.setIsVideoChat(false);
  videoEvent.value = null;
  sessionStore.reset();
  emit("leaveVideo", props.group);
};

const handleTooltipMouseEnter = async (e, text) => {
  if (tooltip.target !== e.target) {
    tooltip.visible = false;
  }
  await nextTick();
  tooltip.target = e.target;
  tooltip.text = text;
  tooltip.initial = "bottom";
  tooltip.xPosition = "center";
  tooltip.targetLeft = false;
  tooltip.visible = true;
};

const handleTooltipMouseLeave = () => {
  tooltip.targetLeft = true;
};

const handleTooltipClose = () => {
  tooltip.visible = false;
  tooltip.targetLeft = false;
  tooltip.target = null;
  tooltip.text = "";
  tooltip.initial = "bottom";
  tooltip.xPosition = "center";
};

const handleReactionEmojiTooltipMouseClick = async (e, messageId) => {
  if (reactionEmojiTooltip.visible) {
    handleReactionEmojiTooltipClose();
    return;
  }

  // Store the message ID being reacted to
  reactingMessageId.value = messageId;

  if (reactionEmojiTooltip.target !== e.target) {
    reactionEmojiTooltip.visible = false;
  }
  await nextTick();
  reactionEmojiTooltip.target = e.target;
  reactionEmojiTooltip.initial = "top";
  reactionEmojiTooltip.xPosition = "right";
  reactionEmojiTooltip.targetLeft = false;
  reactionEmojiTooltip.visible = true;
};

const handleReactionEmojiTooltipClose = () => {
  reactionEmojiTooltip.visible = false;
  reactionEmojiTooltip.targetLeft = false;
  reactionEmojiTooltip.target = null;
  reactionEmojiTooltip.initial = "top";
  reactionEmojiTooltip.xPosition = "right";
  reactingMessageId.value = null;
};

const handleEmojiTooltipMouseClick = async (e) => {
  if (reactionEmojiTooltip.visible) {
    handleReactionEmojiTooltipClose();
    return;
  }

  if (reactionEmojiTooltip.target !== e.target) {
    reactionEmojiTooltip.visible = false;
  }
  await nextTick();
  reactionEmojiTooltip.target = e.target;
  reactionEmojiTooltip.initial = "top";
  reactionEmojiTooltip.xPosition = "right";
  reactionEmojiTooltip.targetLeft = false;
  reactionEmojiTooltip.visible = true;

  textareaEl.value.focus();
  textareaFocused.value = true;
};

const handleLeaveVideo = () => {
  videoStart.value = false;
  livekitConnected.value = false;
  sessionStore.setIsVideoChat(false);
  sessionStore.reset();
  emit('back');
};

const setGroupUsers = () => {
  const { users: groupUsers, pendingUsers } = currentGroup.value;
  const allGroupUsers = [...groupUsers, ...pendingUsers];

  const groupUsersIds = allGroupUsers.map((u) => {
    if (typeof u === "string" || typeof u === "number") return u;
    return u.user_id;
  });

  users.value = props.allUsers.filter(
    (u) =>
      u.user_id !== authStore.userData.id &&
      groupUsersIds.includes(u.user_id) &&
      !u.invisible
  );

  filteredUsers.value = users.value;

  if (props.inSession) {
    otherUsers.value = props.allUsers.filter(
      (u) =>
        u.user_id !== authStore.userData.id &&
        !groupUsersIds.includes(u.user_id) &&
        sessionStore.participants.find((p) => Number.parseInt(p.participantIdentity, 10) === u.user_id) &&
        !u.invisible
    );
  } else {
    otherUsers.value = props.allUsers.filter(
      (u) =>
        u.user_id !== authStore.userData.id &&
        !groupUsersIds.includes(u.user_id) &&
        !u.invisible
    );
  }

  filteredOtherUsers.value = otherUsers.value;
};

const handleHideChat = () => {
  emit("hide-chat");
};

const minimizeOmniChat = () => {
  sessionRef.value?.leaveRoom();
  sessionStore.reset();
  const boxchat = document.getElementById("boxChat");
  boxchat?.querySelector(".e-box")?.classList.remove("e-box--chat-jitsi");
  props.leaveVideo(props.group);
  boxchat?.querySelector(".e-box-minimize")?.removeEventListener("click", minimizeOmniChat);
};

const getUserData = (userId, key) => {
  const userData = users.value.find((user) => user.user_id === userId);
  return userData && userData[key] ? userData[key] : null;
};

const handleUsersRemove = () => {
  if (selectedUsers.value.length) {
    removeUserConfirmation.value = true;
  }
};

const handleUserSearch = (e) => {
  const searchValue = e.target.value;
  if (searchValue) {
    filteredUsers.value = users.value.filter((u) =>
      u.fullName.toLowerCase().includes(searchValue.toLowerCase())
    );
    filteredOtherUsers.value = otherUsers.value.filter((u) =>
      u.fullName.toLowerCase().includes(searchValue.toLowerCase())
    );
  } else {
    filteredUsers.value = users.value;
    filteredOtherUsers.value = otherUsers.value;
  }
};

const handleAddEventListner = () => {
  nextTick(() => {
    const boxchat = document.getElementById("boxChat");
    if (!boxchat) return;
    const eBox = boxchat.querySelector(".e-box");
    const minimizeButton = boxchat.querySelector(".e-box-minimize");
    minimizeButton?.addEventListener("click", minimizeOmniChat);
    boxchat.classList.add("e-box-container--full");
    eBox?.classList.add("e-box--chat-jitsi");
  });
};

const startVideo = (group, event) => {
  const authUser = authStore.userData;
  event.stopPropagation();
  handleAddEventListner();
  videoEvent.value = {
    title: group.name,
    groupId: group._id,
    session_moderator: {
      id: authUser.subdomain_user_id
    },
    video_call_type: 'video_conference',
    original_start_date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    original_end_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    isVideoChat: true,
  };
  sessionStore.setIsVideoChat(true);
  videoStart.value = true;
  emit("startVideo", group);
};

const joinVideo = (group, event) => {
  event.stopPropagation();

  if (videoStart.value && inVideoCall.value) {
    videoStart.value = false;
    livekitConnected.value = false;
    sessionStore.setIsVideoChat(false);
    sessionStore.reset();
    emit("leaveVideo", group);
  } else {
    handleAddEventListner();
    const authUser = authStore.userData;
    videoEvent.value = {
      title: group.name,
      groupId: group._id,
      session_speakers: [{
        id: authUser.subdomain_user_id
      }],
      video_call_type: 'video_conference',
      original_start_date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      original_end_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      isVideoChat: true,
    };
    sessionStore.setIsVideoChat(true);
    videoStart.value = true;
    emit("joinVideo", group);
  }
};

// omnichat stores message text through sanitize-html, which drops every tag and
// escapes the rest as &lt; &gt; &amp;. Undo exactly that, in one pass, and render
// the result as text: a message or a name never becomes markup (XN-4828).
const STORED_ENTITIES = { '&lt;': '<', '&gt;': '>', '&amp;': '&' };
const unescapeStored = (text) => String(text ?? '').replace(/&(?:lt|gt|amp);/g, (e) => STORED_ENTITIES[e]);

const messageText = (m) => {
  let message = unescapeStored(m.message);

  if (
    message.includes("has been removed from the video call") &&
    m.user_id === authStore.userData.id
  ) {
    message = "you have been removed from the video call";
  }

  if (
    message.includes("has joined the video call") &&
    m.user_id === authStore.userData.id
  )
    message = "you have joined the video call";

  if (
    message.includes("has started a video call") &&
    m.user_id === authStore.userData.id
  )
    message = "you have started a video call";

  if (
    message.includes("joined the group chat") &&
    m.user_id === authStore.userData.id
  )
    message = "you have joined the group chat";

  if (
    message.includes("has left the video call") &&
    m.user_id === authStore.userData.id
  )
    message = "you have left the video call";

  return message;
};

const chatUsers = (status, totals = false) => {
  if (!props.group) return "";

  const {
    users: groupJoinedUsers,
    pendingUsers: groupPendingUsers,
    declineUsers: groupDeclinedUsers,
    user_id,
  } = props.group;

  let users = [];

  if (status === "moderator") users = groupJoinedUsers;
  if (status === "pending") users = groupPendingUsers;
  if (status === "declined") users = groupDeclinedUsers;
  if (status === "joined") users = groupJoinedUsers;

  if (status === "joined-small") {
    users = groupJoinedUsers;
    users.push("...");
  }

  users = users
    ? users
        .filter((u) =>
          status === "moderator"
            ? u.user_id === user_id
            : u.user_id !== user_id
        )
        .map((u) => u.fullName)
    : [];

  return totals ? users.length + 1 : users.join(", ");
};

const groupUsersClose = () => {
  modal.title = false;
  modal.groupUsers = false;
};

const groupRemoveOnSuccess = () => {
  removeGroup();
  modal.groupRemove = false;
};

const groupRemoveOnClose = () => {
  modal.groupRemove = false;
};

const groupRemoveOnSave = () => {
  saveMessages();
  modal.groupRemove = false;
};

const openInviteOthersModal = () => {
  selectedUsers.value = [];
  showActions.value = false;
  modal.groupChatParticipants = false;
  modal.groupInviteOthers = true;
};

const groupUsersInviteOnSuccess = () => {
  const groupPendingUsersIds = props.group.pendingUsers.map((u) => u.user_id);
  selectedUsers.value = selectedUsers.value.filter(
    (id) => !groupPendingUsersIds.includes(id)
  );
  if (selectedUsers.value.length) {
    socketStore.emit("user_group_invite", {
      domain: window.store.subdomain,
      group: props.group,
      users: selectedUsers.value,
    });
    modal.groupInviteOthers = false;
    chatParticipants();
  }
};

const groupChatParticipantsOnClose = () => {
  modal.groupChatParticipants = false;
  showActions.value = false;
};

const groupUsersRemoveOnSuccess = () => {
  filteredUsers.value = filteredUsers.value.filter(
    (u) => !selectedUsers.value.includes(u.user_id)
  );
  exitGroup(selectedUsers.value);
  selectedUsers.value = [];
  removeUserConfirmation.value = false;
  modal.groupUsersRemove = false;
};

const groupUsersInviteOnClose = () => {
  modal.groupInviteOthers = false;
  selectedUsers.value = [];
  setGroupUsers();
  modal.groupChatParticipants = true;
};

const getDate = (fullDate, format = "HH:mm") => {
  const luxonDate = DateTime.fromISO(fullDate);
  return luxonDate.toFormat(format);
};

const getDateString = (fullDate) => {
  const luxonDate = DateTime.fromISO(fullDate);
  const dateString = luxonDate.toRelativeCalendar();

  if (dateString === "today" || dateString === "yesterday")
    return dateString;

  return compactDate(fullDate, { long: true });
};

const handleEnter = (e) => {
  if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    sendMessage();
    textareaFocused.value = true;
  }
};

const handleKeyDown = (e) => {
  setTimeout(() => {
    const textarea = e.target;
    textarea.style.cssText = "height: 0";
    const height = Math.min(20 * 5, textarea.scrollHeight);
    textarea.style.cssText = `height: ${height}px`;
  }, 0);
};

const sendMessage = async () => {
  // Belt-and-braces with the disabled composer below: Enter, the send button
  // and any future caller all funnel through here. The websocket rejects these
  // too — this just avoids a pointless round-trip.
  if (isSendingBlocked.value) return;

  if (message.value) {
    const messageText = message.value
      .split('\n')
      .map(line => line.replace(/\s+/g, ' '))
      .filter(line => line.trim() !== '')
      .join('\n');

    if (messageText.length > 0) {
      const { _id } = props.group;
      const payload = {
        domain: window.store.subdomain,
        group: _id,
        user_id: authStore.userData.id,
        message: messageText,
      };

      socketStore.emit("user_group_message", payload);

      // Clear the input immediately — never let a later error strand the text
      // (the message text is already captured in `messageText` above).
      message.value = "";
      // OmniChat owns the group: it appends the message optimistically.
      emit("message-sent", messageText);
      try {
        await saveInteractionData(authStore.userData, "omnichat-send-message");
      } catch (error) {
        console.error(error);
      }
    }
  }
  textareaEl.value.focus();
  textareaFocused.value = true;
};

const saveMessages = () => {
  let {
    group,
    group: { messages },
  } = props;

  if (messages.length > 0) {
    messages = messages.map((m) => {
      return m.user && m.user.fullName
        ? `${numericDate(m.created_at)} ${getDate(m.created_at, "HH:mm:ss")}  ${
            m.user.fullName
          }: ${m.message}`
        : `NO USER: ${m.message}`;
    });
  }

  const blob = new Blob(
    [messages.join("\r\n\n").replace(/(<([^>]+)>)/gi, "")],
    {
      type: "text/plain;charset=utf-8",
    }
  );

  const now = new Date();
  const date = shortDate(now);
  const time = timeOfDay(now);

  saveAs(blob, `${group.name} OmniChat - ${date} ${time}.TXT`);
  showActions.value = false;
};

const renameGroup = () => {
  showGroupNameModal.value = true;
};

const removeGroup = () => {
  const { group } = props;

  group.domain = window.store.subdomain;

  socketStore.emit("user_group_remove", group);
  emit('view-update', 'groups');
};

const onGroupNameConfirm = (newName) => {
  if (newName && newName !== "") {
    emit('rename-group', newName);
    socketStore.emit(
      "user_group_update",
      {
        domain: window.store.subdomain,
        groupData: props.group,
        action: "group_name",
        groupId: props.group._id,
        name: newName,
      },
      (response) => {
        // if (response.status) props.closeGroup(false);
      }
    );
  }
  showGroupNameModal.value = false;
  showActions.value = false;
};

const onGroupNameCancel = () => {
  showGroupNameModal.value = false;
  showActions.value = false;
};

const exitGroup = (selectedUsers = []) => {
  modal.groupLeaveChat = false;
  const { group } = props;
  const action = selectedUsers.length > 0 ? "removed" : "left";
  const users = selectedUsers.length > 0 ? selectedUsers : [authStore.userData.id];

  showActions.value = false;

  if (group._id) {
    const args = {
      domain: window.store.subdomain,
      groupData: group,
      action: "group_leave",
      groupId: group._id,
      users: users,
      videoUsers: group.videoUsers.filter((id) => id !== authStore.userData.id),
    };

    if (users.length === 1 && users[0] === authStore.userData.id) {
      emit("attendee-left", group._id);
    }

    socketStore.emit("user_group_update", args, (response) => {
      if (response.success) {
        if (users.length > 1) {
          filteredUsers.value = filteredUsers.value.filter(
            (user) => !users.includes(user.user_id)
          );
          selectedUsers.value = [];
        }
      }
    });

    const otherUsers =
      selectedUsers && selectedUsers.length > 0
        ? group.users.filter(
            (u) =>
              selectedUsers.includes(u.user_id) &&
              u.user_id !== authStore.userData.id
          )
        : [];

    if (action === "left") {
      socketStore.emit("user_group_message", {
        domain: window.store.subdomain,
        group: group._id,
        user_id: authStore.userData.id,
        message: `left the group chat`,
      });
    } else {
      otherUsers.forEach((u) => {
        socketStore.emit("user_group_message", {
          domain: window.store.subdomain,
          group: group._id,
          user_id: u.user_id,
          message: `was removed from the group chat`,
        });
      });
    }
    if (!selectedUsers.length) {
      emit("view-update", "groups");
    }
  }
};

const chatParticipants = () => {
  selectedUsers.value = [];
  setGroupUsers();
  modal.groupChatParticipants = true;
  showActions.value = false;
};

const handleBack = () => {
  videoStart.value = false;
  livekitConnected.value = false;
  message.value = null;
  emit("back");
};

const handleSelectEmoji = async (emoji) => {
  const codePoints = emoji.r.split('-').map(cp => Number.parseInt(cp, 16));
  const emojiChar = String.fromCodePoint(...codePoints);
  
  const textarea = textareaEl.value;
  const start = textarea.selectionStart || 0;
  const end = textarea.selectionEnd || 0;
  const currentValue = message.value || '';
  
  // Insert emoji at cursor position
  message.value = currentValue.substring(0, start) + emojiChar + currentValue.substring(end);
  
  // Set cursor position after the inserted emoji
  await nextTick();
  const newPosition = start + emojiChar.length;
  textarea.setSelectionRange(newPosition, newPosition);
  textarea.focus();
};

const handleReactionEmoji = async (emoji) => {
  if (!reactingMessageId.value) {
    handleSelectEmoji(emoji);
    await nextTick();
    textareaEl.value.focus();
    textareaFocused.value = true;
    return;
  }
  
  // Convert emoji unicode code points to character for display purposes
  const emojiCode = emoji.r; // Store the hex code
  
  // Emit socket event
  socketStore.emit("message_reaction", {
    messageId: reactingMessageId.value,
    emoji: emojiCode,
    userId: authStore.userData.id,
    groupId: props.group._id,
    // The hub, as every other chat event names it. `authStore.domain` does not
    // exist: the reaction went out with no hub, the server could not place
    // the caller in one and dropped it — no reaction for anybody (Sessions →
    // In-session chat → "Reacting to a message shows no reaction").
    domain: window.store.subdomain,
  });
  
  handleReactionEmojiTooltipClose();
};

// Convert emoji hex code to actual emoji character
const getEmojiChar = (hexCode) => {
  try {
    const codePoints = hexCode.split('-').map(cp => Number.parseInt(cp, 16));
    return String.fromCodePoint(...codePoints);
  } catch (e) {
    return '❓';
  }
};

// Group reactions by emoji and count occurrences
const getGroupedReactions = (reactions) => {
  if (!reactions || !reactions.length) return [];
  
  const grouped = {};
  reactions.forEach(r => {
    if (!grouped[r.emoji]) {
      grouped[r.emoji] = {
        emoji: r.emoji,
        count: 0,
        userReacted: false
      };
    }
    grouped[r.emoji].count++;
    if (r.user_id === authStore.userData.id) {
      grouped[r.emoji].userReacted = true;
    }
  });
  
  const result = Object.values(grouped).sort((a, b) => b.count - a.count);
  if (result.length > 5) {
    const mainReactions = result.slice(0, 5);
    const moreCount = result.length - 5;
    return [
      ...mainReactions,
      { isMore: true, count: moreCount }
    ];
  }
  
  return result;
};

// Toggle reaction (add or remove)
const toggleReaction = (messageId, emoji) => {
  socketStore.emit("message_reaction", {
    messageId,
    emoji,
    userId: authStore.userData.id,
    groupId: props.group._id,
    // The hub, as every other chat event names it. `authStore.domain` does not
    // exist: the reaction went out with no hub, the server could not place
    // the caller in one and dropped it — no reaction for anybody (Sessions →
    // In-session chat → "Reacting to a message shows no reaction").
    domain: window.store.subdomain,
  });
};

onMounted(async () => {
  await nextTick();
  setTimeout(() => {
    if (textareaEl.value) {
      textareaEl.value.focus();
      textareaFocused.value = true;
    }
  }, 300);
  
  if (props.group) {
    const messagesContainer = document.querySelector(
      `#group-${props.group._id} ul`
    );
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  };

  socketListeners.on("groups_refresh", ({ group }) => {
    if (group && group.domain === window.store.subdomain && group._id === props.group._id) {
      currentGroup.value.users = group.users;
      currentGroup.value.pendingUsers = group.pendingUsers;
      setGroupUsers();
    }
  });

  socketListeners.on("message_reaction_update", ({ messageId, groupId, reactions }) => {
    if (groupId === props.group._id && props.group.messages) {
      const messageIndex = props.group.messages.findIndex(m => m._id === messageId);
      if (messageIndex !== -1) {
        emit('reactions-update', messageIndex, reactions);
      }
    }
  });

  currentGroup.value = props.group;
  setGroupUsers();
});

defineExpose({
  handleLeaveVideo,
  videoStart
});
</script>

<template>
  <div class="messages xl-rm" v-if="group" :id="`group-${group._id}`">
    <tooltip
      v-if="tooltip.visible"
      :text="tooltip.text"
      :initial="tooltip.initial"
      :x-position="tooltip.xPosition"
      :target-left="tooltip.targetLeft"
      :rect="tooltip.target?.getBoundingClientRect()"
      @close="handleTooltipClose"
    />

    <pge-emoji-picker
      v-if="reactionEmojiTooltip.visible"
      persistent
      :initial="reactionEmojiTooltip.initial"
      :x-position="reactionEmojiTooltip.xPosition"
      :target-left="reactionEmojiTooltip.targetLeft"
      :rect="reactionEmojiTooltip.target?.getBoundingClientRect()"
      @close="handleReactionEmojiTooltipClose"
      @select-emoji="handleReactionEmoji"
    />
    <!-- In a session this is the session's one conversation: the panel header
         already names it, and there is no list to go back to. -->
    <div v-if="!inSession" class="messages__header">
      <div class="messages__profiles">
        <div class="messages__profile__actions">
          <button
            v-if="template === 'default' || videoStart"
            class="messages__profiles--btn"
            @click="handleBack"
            @mouseenter="(e) => handleTooltipMouseEnter(e, 'Back')"
            @mouseleave="handleTooltipMouseLeave"
          >
            <icon-back />
            <notifications-count
              class="messages__profiles--btn--notifs"
              :count="allNotifications"
            />
          </button>
        </div>
        <div class="messages__profiles--names truncate">
          {{ group.name }}
        </div>
      </div>
      <div class="messages__actions">
        <template v-if="!inSession && !isSessionGroupWithActiveSession">
          <div class="messages__actions__container" v-if="!group.videoUrl || group.videoUsers.length === 0">
            <button
              class="messages__actions__btn"
              @click="(event) => startVideo(group, event)"
              @mouseenter="(e) => handleTooltipMouseEnter(e, 'Video Chat')"
              @mouseleave="handleTooltipMouseLeave"
            >
              <icon-video />
            </button>
          </div>
          <template v-else>
            <div class="messages__actions__container" v-if="videoStart && jitsi.start">
              <button
                class="messages__actions__btn"
                @click="handleLeaveVideo"
                @mouseenter="(e) => handleTooltipMouseEnter(e, 'Video Chat Off')"
                @mouseleave="handleTooltipMouseLeave"
              >
                <icon-video-off />
              </button>
            </div>
            <div class="messages__actions__container" v-else>
              <button
                class="messages__actions__btn"
                style="background-color: transparent; border: transparent"
                @click="(event) => joinVideo(group, event)"
                @mouseenter="(e) => handleTooltipMouseEnter(e, 'Join Video Chat')"
                @mouseleave="handleTooltipMouseLeave"
              >
                <icon-video />
              </button>
            </div>
          </template>
          <div class="messages__actions__container" v-if="videoStart && jitsi.start">
            <button
              class="messages__actions__btn"
              @click="handleHideChat"
              @mouseenter="(e) => handleTooltipMouseEnter(e, isChatHidden ? 'Show Text Chat' : 'Hide Text Chat')"
              @mouseleave="handleTooltipMouseLeave"
            >
              <template v-if="isChatHidden">
                <icon-message-on />
              </template>
              <template v-else>
                <icon-message-off />
              </template>
            </button>
          </div>
        </template>
        <dropdown
          :can-remove="canRemove"
          :rename-group="renameGroup"
          :chat-participants="chatParticipants"
          :group="group"
          :save-messages="saveMessages"
          :remove-group="() => (modal.groupRemove = true)"
          :exit-group="() => (modal.groupLeaveChat = true)"
          :in-session="inSession"
        />
      </div>
    </div>
    <div class="messages__block">
      <SessionLoadingSkeleton v-show="videoStart && !livekitConnected" :with-chat="false" />
      <Session
        v-if="videoStart"
        ref="sessionRef"
        v-show="livekitConnected"
        :event="videoEvent"
        style="border-right: 1px solid #ccc;"
        @ready="handleSessionReady"
        @connected="handleSessionConnected"
        @disconnected="handleSessionDisconnected"
        @close="handleSessionClose"
      />
      <div v-if="!isChatHidden" class="messages__block__content" :class="{ 'is-phone-chat': phoneChat }" :style="{maxWidth: videoStart && jitsi.start ? '430px' : '100%'}">
        <ul class="messages__block__content__list no-scrollbar">
          <!-- A phone's empty chat says who reads it (prototype empty state). -->
          <li v-if="phoneChat && !hasMessages" class="chat-empty" data-testid="chat-empty">
            <b>No messages yet</b>
            <p>Anything you type here is seen by everyone in the session.</p>
          </li>
          <li v-for="(dateMessages, date) in messages" :key="date">
            <div class="messages__date">
              <span>{{ getDateString(dateMessages[0].created_at) }}</span>
            </div>
            <ul>
              <li v-for="m in dateMessages" :key="m._id">
                <div v-if="m._id === firstNewId" class="newmark" data-testid="chat-new-line">New</div>
                <div class="message__container">
                  <div v-if="!m.you" class="message__container--avatar">
                    <img
                      v-if="m.user.avatar && !failedAvatars[m.user.avatar]"
                      :src="m.user.avatar"
                      :alt="m.user.fullName"
                      :title="m.user.fullName"
                      @error="onAvatarError(m.user.avatar)"
                    />
                    <svg
                      v-else
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21.6494 19.8756C20.2216 17.4072 18.0212 15.6372 15.4534 14.7981C16.7236 14.042 17.7104 12.8898 18.2624 11.5185C18.8143 10.1473 18.9009 8.63272 18.5088 7.20749C18.1167 5.78226 17.2676 4.52515 16.0918 3.62921C14.9161 2.73327 13.4788 2.24805 12.0006 2.24805C10.5224 2.24805 9.08512 2.73327 7.90939 3.62921C6.73367 4.52515 5.88455 5.78226 5.49244 7.20749C5.10033 8.63272 5.1869 10.1473 5.73886 11.5185C6.29082 12.8898 7.27765 14.042 8.54781 14.7981C5.98 15.6362 3.77968 17.4062 2.35187 19.8756C2.29951 19.961 2.26478 20.056 2.24973 20.155C2.23468 20.254 2.23961 20.355 2.26424 20.4521C2.28886 20.5492 2.33268 20.6404 2.39311 20.7202C2.45354 20.8001 2.52936 20.867 2.61608 20.9171C2.70281 20.9672 2.79869 20.9995 2.89806 21.0119C2.99744 21.0243 3.09831 21.0167 3.1947 20.9896C3.2911 20.9624 3.38108 20.9162 3.45933 20.8537C3.53758 20.7912 3.60251 20.7136 3.65031 20.6256C5.41656 17.5731 8.53843 15.7506 12.0006 15.7506C15.4628 15.7506 18.5847 17.5731 20.3509 20.6256C20.3987 20.7136 20.4637 20.7912 20.5419 20.8537C20.6202 20.9162 20.7101 20.9624 20.8065 20.9896C20.9029 21.0167 21.0038 21.0243 21.1032 21.0119C21.2026 20.9995 21.2984 20.9672 21.3852 20.9171C21.4719 20.867 21.5477 20.8001 21.6081 20.7202C21.6686 20.6404 21.7124 20.5492 21.737 20.4521C21.7616 20.355 21.7666 20.254 21.7515 20.155C21.7365 20.056 21.7017 19.961 21.6494 19.8756ZM6.75062 9.0006C6.75062 7.96225 7.05853 6.94721 7.6354 6.08385C8.21228 5.2205 9.03222 4.54759 9.99153 4.15023C10.9508 3.75287 12.0064 3.6489 13.0248 3.85147C14.0432 4.05405 14.9787 4.55406 15.7129 5.28829C16.4472 6.02251 16.9472 6.95797 17.1497 7.97637C17.3523 8.99477 17.2483 10.0504 16.851 11.0097C16.4536 11.969 15.7807 12.7889 14.9174 13.3658C14.054 13.9427 13.039 14.2506 12.0006 14.2506C10.6087 14.2491 9.2742 13.6955 8.28995 12.7113C7.30571 11.727 6.75211 10.3925 6.75062 9.0006Z"
                        fill="#4B444D"
                      />
                    </svg>
                  </div>
                  <div class="message__container__text__container">
                    <div
                      class="message__container__text__container--text"
                      :class="{ you: m.you }"
                      @mouseenter="hoveredMessageId = m.id || m.created_at"
                      @mouseleave="hoveredMessageId = null"
                    >
                      <div
                        :class="{
                          'message__container__text__container--text--message': true,
                          you: m.you,
                        }"
                      >
                        <div class="bubble-container">
                          <strong>{{ m.user.fullName }}<span v-if="phoneChat && isHostMessage(m)" class="host-mark" data-testid="chat-host-mark"> · Host</span></strong>
                          <div class="bubble">
                            <div>{{ messageText(m) }}</div>
                          </div>
                          <!-- Reactions display -->
                          <div
                            v-if="m.reactions && m.reactions.length > 0"
                            class="message__container__text__container--reactions"
                            :class="{ you: m.you }"
                          >
                            <span
                              v-for="(reaction, idx) in getGroupedReactions(m.reactions)"
                              :key="idx"
                              class="reaction-badge"
                              :class="{ 'user-reacted': reaction.userReacted, 'more-badge': reaction.isMore }"
                              @click="!reaction.isMore && toggleReaction(m._id, reaction.emoji)"
                            >
                              <template v-if="reaction.isMore">
                                <span class="reaction-badge--count">
                                  +{{ reaction.count }}
                                </span>
                              </template>
                              <template v-else>
                                {{ getEmojiChar(reaction.emoji) }}
                                <span v-if="reaction.count > 1" class="reaction-badge--count">
                                  {{ reaction.count }}
                                </span>
                              </template>
                            </span>
                          </div>

                          <div
                            class="message__container__text__container--time"
                            :class="{ you: m.you }"
                          >
                            <span>{{ getDate(m.created_at, "h:mm a") }}</span>
                          </div>
                        </div>
                      </div>

                      <pge-button
                        v-if="hoveredMessageId === (m.id || m.created_at)"
                        template="text-neutral"
                        label=""
                        icon="EmojiPickerReaction"
                        button-size="rounded-full"
                        tooltip="React"
                        tooltip-x-position="center"
                        :custom-style="{ marginTop: !m.you ? '10px' : '0' }"
                        @click="(e) => handleReactionEmojiTooltipMouseClick(e, m._id)"
                      />
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </li>
        </ul>
        <div ref="formEl" class="form">
          <div v-if="isSendingBlocked" class="form__disabled-notice">
            In-session chat is turned off for this session.
          </div>
          <div v-else class="form__message" :class="{ focused: textareaFocused }">
            <!-- Emoji first, as in the design's composer: emoji, input, send. -->
            <pge-button
              class="form__emoji"
              :class="{ 'is-on': reactionEmojiTooltip.visible }"
              :template="reactionEmojiTooltip.visible ? 'text' : 'text-neutral'"
              label=""
              icon="EmojiPicker"
              button-size="rounded-full"
              tooltip="Add Emojies"
              tooltip-x-position="center"
              @click="handleEmojiTooltipMouseClick"
            />
            <textarea
              ref="textareaEl"
              v-model="message"
              :placeholder="phoneChat ? 'Message everyone' : 'Type a message'"
              aria-label="Message"
              @keydown="handleKeyDown"
              @keydown.enter="handleEnter"
              @focus="textareaFocused = true"
            />
            <pge-button
              class="form__send-btn"
              template="text-neutral"
              label=""
              icon="Send"
              button-size="rounded-full"
              @click="sendMessage"
            />
          </div>
        </div>

        <Transition name="emoji-slide">
          <div
            v-if="emojiTooltip.visible"
            ref="emojiPickerEl"
            class="xl-emoji-picker__wrapper no-scrollbar"
          >
            <EmojiPicker
              native
              hide-search
              hide-group-icons
              disable-skin-tones
              class="xl-emoji-picker"
              @select="handleSelectEmoji"
            />
          </div>
        </Transition>
      </div>
    </div>

    <ModalComponent
      v-if="modal.groupRemove"
      on-close-text="Cancel"
      on-success-text="Delete"
      :on-save="groupRemoveOnSave"
      on-save-text="Save group text chat"
      close-header-button
      @success="groupRemoveOnSuccess"
      @close="groupRemoveOnClose"
    >
      <template #header>
        <div class="modal__header__title">
          <h3>Delete Chat</h3>
        </div>
      </template>
      <div class="modal__content--no-header">
        <p>
          Are you sure you want to delete this group? You may want to first save
          the group text chat.
        </p>

        <pge-button
          template="text-neutral"
          label="Save chat text"
          icon="Save"
          button-size="small"
          @click="groupRemoveOnSave"
        />
      </div>
    </ModalComponent>

    <ModalComponent
      v-if="modal.groupChatParticipants"
      close-header-button
      :custom-footer="canRemove"
      @close="groupChatParticipantsOnClose"
    >
      <template #header>
        <div class="modal__header__title">
          <icon-chat-participants />
          <h3>Chat Participants</h3>
        </div>
      </template>
      <div v-if="!usersLoading" class="modal__content--with-header">
        <div class="search">
          <search-input
            placeholder="Search users"
            focused
            @input="handleUserSearch"
          />
        </div>
        <ul
          v-if="filteredUsers.length"
          class="modal__content--with-header--border-bottom flex-col"
          :class="{
            'no-footer': !canRemove
          }"
        >
          <li
            v-for="(user, index) in filteredUsers"
            :key="`${user.user_id}-${index}`"
            class="custom-check group__users"
            :class="{ selected: selectedUsers.includes(user.user_id) }"
          >
            <checkbox-input
              v-if="canRemove"
              v-model="selectedUsers"
              :value="user.user_id"
              :name="`user-${user.user_id}`"
              :id="`user-${user.user_id}`"
              multiple
            />
            <label :for="`user-${user.user_id}`" class="group__users__wrapper">
              <div class="group__users__wrapper__content">
                <div class="group__users__wrapper__content__avatar">
                  <div class="group__users__wrapper__content__avatar--image">
                    <img
                      v-if="user.avatar"
                      :src="user.avatar"
                      :alt="user.fullName"
                    />
                    <svg
                      v-else
                      width="40"
                      height="40"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="0.25"
                        y="0.25"
                        width="39.5"
                        height="39.5"
                        rx="19.75"
                        stroke="#CEC3CE"
                        stroke-width="0.5"
                      />
                      <path
                        d="M29.6503 27.8756C28.2225 25.4072 26.0222 23.6372 23.4544 22.7981C24.7246 22.042 25.7114 20.8898 26.2634 19.5185C26.8153 18.1473 26.9019 16.6327 26.5098 15.2075C26.1177 13.7823 25.2685 12.5252 24.0928 11.6292C22.9171 10.7333 21.4798 10.248 20.0016 10.248C18.5234 10.248 17.0861 10.7333 15.9104 11.6292C14.7346 12.5252 13.8855 13.7823 13.4934 15.2075C13.1013 16.6327 13.1879 18.1473 13.7398 19.5185C14.2918 20.8898 15.2786 22.042 16.5488 22.7981C13.981 23.6362 11.7807 25.4062 10.3528 27.8756C10.3005 27.961 10.2658 28.056 10.2507 28.155C10.2357 28.254 10.2406 28.355 10.2652 28.4521C10.2898 28.5492 10.3337 28.6404 10.3941 28.7202C10.4545 28.8001 10.5303 28.867 10.6171 28.9171C10.7038 28.9672 10.7997 28.9995 10.899 29.0119C10.9984 29.0243 11.0993 29.0167 11.1957 28.9896C11.2921 28.9624 11.3821 28.9162 11.4603 28.8537C11.5386 28.7912 11.6035 28.7136 11.6513 28.6256C13.4175 25.5731 16.5394 23.7506 20.0016 23.7506C23.4638 23.7506 26.5857 25.5731 28.3519 28.6256C28.3997 28.7136 28.4646 28.7912 28.5429 28.8537C28.6211 28.9162 28.7111 28.9624 28.8075 28.9896C28.9039 29.0167 29.0048 29.0243 29.1042 29.0119C29.2035 28.9995 29.2994 28.9672 29.3861 28.9171C29.4729 28.867 29.5487 28.8001 29.6091 28.7202C29.6695 28.6404 29.7134 28.5492 29.738 28.4521C29.7626 28.355 29.7675 28.254 29.7525 28.155C29.7374 28.056 29.7027 27.961 29.6503 27.8756ZM14.7516 17.0006C14.7516 15.9622 15.0595 14.9472 15.6364 14.0839C16.2133 13.2205 17.0332 12.5476 17.9925 12.1502C18.9518 11.7529 20.0074 11.6489 21.0258 11.8515C22.0442 12.054 22.9797 12.5541 23.7139 13.2883C24.4481 14.0225 24.9481 14.958 25.1507 15.9764C25.3533 16.9948 25.2493 18.0504 24.852 19.0097C24.4546 19.969 23.7817 20.7889 22.9183 21.3658C22.055 21.9427 21.0399 22.2506 20.0016 22.2506C18.6097 22.2491 17.2752 21.6955 16.2909 20.7113C15.3067 19.727 14.7531 18.3925 14.7516 17.0006Z"
                        fill="#4B444D"
                      />
                    </svg>
                  </div>
                  <span
                    :class="{
                      users__status: true,
                      'users__status--online': user.online,
                      'users__status--offline': !user.online,
                    }"
                  ></span>
                </div>
                <span class="group__users__wrapper__content--name">
                  {{ user.fullName }}
                </span>
              </div>
            </label>
            <button class="users__info_button" @click="() => userShow(user)">
              <icon-info size="16" color="#B3B3B3" />
            </button>
          </li>
        </ul>

        <div
          v-else-if="!users.length || !filteredUsers.length"
          class="modal__content--no-data"
          :class="{
            'no-footer': !canRemove,
          }"
        >
          <span v-if="!users.length">No attendees yet</span>
          <span v-else-if="users.length && !filteredUsers.length">
            No matching attendees found
          </span>
        </div>
      </div>
      <template #footer>
        <div class="modal__custom-footer">
          <pge-button
            label="Remove"
            template="text-error"
            icon="UserRemove"
            size="18"
            button-size="small"
            :disabled="!selectedUsers.length"
            @click="handleUsersRemove"
          />
          <pge-button
            label="Invite Others"
            template="filled"
            icon="UserCreate"
            size="18"
            button-size="small"
            @click="openInviteOthersModal"
          />
        </div>
      </template>
    </ModalComponent>

    <ModalComponent
      v-if="modal.groupInviteOthers"
      on-close-text="Cancel"
      on-success-text="Invite"
      close-header-button
      :is-success-button-disabled="!selectedUsers.length"
      :is-content-loading="usersLoading"
      @success="groupUsersInviteOnSuccess"
      @close="groupUsersInviteOnClose"
    >
      <template #header>
        <div class="modal__header__title">
          <icon-user-create />
          <h3>Invite Members</h3>
        </div>
      </template>
      <loading v-if="usersLoading" variant="rows" :rows="5" />
      <div v-else class="modal__content--with-header">
        <div class="search">
          <search-input
            placeholder="Search users"
            focused
            @input="handleUserSearch"
          />
        </div>
        <ul
          v-if="otherUsers.length"
          class="modal__content--with-header--border-bottom flex-col"
        >
          <li
            v-for="(user, index) in filteredOtherUsers"
            :key="`${user.user_id}-${index}`"
            class="custom-check group__users"
            :class="{ selected: selectedUsers.includes(user.user_id) }"
          >
            <checkbox-input
              v-model="selectedUsers"
              :value="user.user_id"
              :name="`user-${user.user_id}`"
              :id="`user-${user.user_id}`"
              multiple
            />
            <label :for="`user-${user.user_id}`" class="group__users__wrapper">
              <div class="group__users__wrapper__content">
                <div class="group__users__wrapper__content__avatar">
                  <div class="group__users__wrapper__content__avatar--image">
                    <img
                      v-if="user.avatar"
                      :src="user.avatar"
                      :alt="user.fullName"
                    />
                    <svg
                      v-else
                      width="40"
                      height="40"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="0.25"
                        y="0.25"
                        width="39.5"
                        height="39.5"
                        rx="19.75"
                        stroke="#CEC3CE"
                        stroke-width="0.5"
                      />
                      <path
                        d="M29.6503 27.8756C28.2225 25.4072 26.0222 23.6372 23.4544 22.7981C24.7246 22.042 25.7114 20.8898 26.2634 19.5185C26.8153 18.1473 26.9019 16.6327 26.5098 15.2075C26.1177 13.7823 25.2685 12.5252 24.0928 11.6292C22.9171 10.7333 21.4798 10.248 20.0016 10.248C18.5234 10.248 17.0861 10.7333 15.9104 11.6292C14.7346 12.5252 13.8855 13.7823 13.4934 15.2075C13.1013 16.6327 13.1879 18.1473 13.7398 19.5185C14.2918 20.8898 15.2786 22.042 16.5488 22.7981C13.981 23.6362 11.7807 25.4062 10.3528 27.8756C10.3005 27.961 10.2658 28.056 10.2507 28.155C10.2357 28.254 10.2406 28.355 10.2652 28.4521C10.2898 28.5492 10.3337 28.6404 10.3941 28.7202C10.4545 28.8001 10.5303 28.867 10.6171 28.9171C10.7038 28.9672 10.7997 28.9995 10.899 29.0119C10.9984 29.0243 11.0993 29.0167 11.1957 28.9896C11.2921 28.9624 11.3821 28.9162 11.4603 28.8537C11.5386 28.7912 11.6035 28.7136 11.6513 28.6256C13.4175 25.5731 16.5394 23.7506 20.0016 23.7506C23.4638 23.7506 26.5857 25.5731 28.3519 28.6256C28.3997 28.7136 28.4646 28.7912 28.5429 28.8537C28.6211 28.9162 28.7111 28.9624 28.8075 28.9896C28.9039 29.0167 29.0048 29.0243 29.1042 29.0119C29.2035 28.9995 29.2994 28.9672 29.3861 28.9171C29.4729 28.867 29.5487 28.8001 29.6091 28.7202C29.6695 28.6404 29.7134 28.5492 29.738 28.4521C29.7626 28.355 29.7675 28.254 29.7525 28.155C29.7374 28.056 29.7027 27.961 29.6503 27.8756ZM14.7516 17.0006C14.7516 15.9622 15.0595 14.9472 15.6364 14.0839C16.2133 13.2205 17.0332 12.5476 17.9925 12.1502C18.9518 11.7529 20.0074 11.6489 21.0258 11.8515C22.0442 12.054 22.9797 12.5541 23.7139 13.2883C24.4481 14.0225 24.9481 14.958 25.1507 15.9764C25.3533 16.9948 25.2493 18.0504 24.852 19.0097C24.4546 19.969 23.7817 20.7889 22.9183 21.3658C22.055 21.9427 21.0399 22.2506 20.0016 22.2506C18.6097 22.2491 17.2752 21.6955 16.2909 20.7113C15.3067 19.727 14.7531 18.3925 14.7516 17.0006Z"
                        fill="#4B444D"
                      />
                    </svg>
                  </div>
                  <span
                    :class="{
                      users__status: true,
                      'users__status--online': user.online,
                      'users__status--offline': !user.online,
                    }"
                  ></span>
                </div>
                <span class="group__users__wrapper__content--name">{{
                  user.fullName
                }}</span>
              </div>
            </label>
            <button class="users__info_button" @click="() => userShow(user)">
              <icon-info size="16" color="#B3B3B3" />
            </button>
          </li>
        </ul>

        <div
          v-else-if="!usersLoading && (!otherUsers.length || !filteredOtherUsers.length)"
          class="modal__content--no-data"
        >
          <span v-if="!otherUsers.length">No attendees to invite</span>
          <span v-else-if="otherUsers.length && !filteredOtherUsers.length">
            No matching attendees found
          </span>
        </div>
      </div>
    </ModalComponent>

    <ModalComponent
      v-if="removeUserConfirmation"
      on-close-text="Cancel"
      on-success-text="Remove"
      width="296px"
      :header="false"
      @success="groupUsersRemoveOnSuccess"
      @close="() => (removeUserConfirmation = false)"
    >
      <div class="modal__content__remove__users">
        <h2>
          {{
            selectedUsers.length > 1
              ? "Remove These Members"
              : "Remove This Member"
          }}
        </h2>
        <div class="modal__content__remove__users__list">
          <div v-for="(user, index) in selectedUsers" :key="index">
            <img
              v-if="getUserData(user, 'avatar')"
              :src="getUserData(user, 'avatar')"
              :alt="getUserData(user, 'fullName')"
            />
            <span v-else>
              <svg
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="0.25"
                  y="0.25"
                  width="39.5"
                  height="39.5"
                  rx="19.75"
                  stroke="#CEC3CE"
                  stroke-width="0.5"
                />
                <path
                  d="M29.6503 27.8756C28.2225 25.4072 26.0222 23.6372 23.4544 22.7981C24.7246 22.042 25.7114 20.8898 26.2634 19.5185C26.8153 18.1473 26.9019 16.6327 26.5098 15.2075C26.1177 13.7823 25.2685 12.5252 24.0928 11.6292C22.9171 10.7333 21.4798 10.248 20.0016 10.248C18.5234 10.248 17.0861 10.7333 15.9104 11.6292C14.7346 12.5252 13.8855 13.7823 13.4934 15.2075C13.1013 16.6327 13.1879 18.1473 13.7398 19.5185C14.2918 20.8898 15.2786 22.042 16.5488 22.7981C13.981 23.6362 11.7807 25.4062 10.3528 27.8756C10.3005 27.961 10.2658 28.056 10.2507 28.155C10.2357 28.254 10.2406 28.355 10.2652 28.4521C10.2898 28.5492 10.3337 28.6404 10.3941 28.7202C10.4545 28.8001 10.5303 28.867 10.6171 28.9171C10.7038 28.9672 10.7997 28.9995 10.899 29.0119C10.9984 29.0243 11.0993 29.0167 11.1957 28.9896C11.2921 28.9624 11.3821 28.9162 11.4603 28.8537C11.5386 28.7912 11.6035 28.7136 11.6513 28.6256C13.4175 25.5731 16.5394 23.7506 20.0016 23.7506C23.4638 23.7506 26.5857 25.5731 28.3519 28.6256C28.3997 28.7136 28.4646 28.7912 28.5429 28.8537C28.6211 28.9162 28.7111 28.9624 28.8075 28.9896C28.9039 29.0167 29.0048 29.0243 29.1042 29.0119C29.2035 28.9995 29.2994 28.9672 29.3861 28.9171C29.4729 28.867 29.5487 28.8001 29.6091 28.7202C29.6695 28.6404 29.7134 28.5492 29.738 28.4521C29.7626 28.355 29.7675 28.254 29.7525 28.155C29.7374 28.056 29.7027 27.961 29.6503 27.8756ZM14.7516 17.0006C14.7516 15.9622 15.0595 14.9472 15.6364 14.0839C16.2133 13.2205 17.0332 12.5476 17.9925 12.1502C18.9518 11.7529 20.0074 11.6489 21.0258 11.8515C22.0442 12.054 22.9797 12.5541 23.7139 13.2883C24.4481 14.0225 24.9481 14.958 25.1507 15.9764C25.3533 16.9948 25.2493 18.0504 24.852 19.0097C24.4546 19.969 23.7817 20.7889 22.9183 21.3658C22.055 21.9427 21.0399 22.2506 20.0016 22.2506C18.6097 22.2491 17.2752 21.6955 16.2909 20.7113C15.3067 19.727 14.7531 18.3925 14.7516 17.0006Z"
                  fill="#4B444D"
                />
              </svg>
            </span>

            <span class="user__name truncate">
              {{ getUserData(user, "fullName") }}
            </span>
          </div>
        </div>
      </div>
    </ModalComponent>

    <ModalComponent
      v-if="modal.groupLeaveChat"
      on-close-text="Cancel"
      on-success-text="Leave"
      :header="false"
      @success="exitGroup"
      @close="() => (modal.groupLeaveChat = false)"
    >
      <div class="modal__content__leave__chat">
        <h2>Leave Chat?</h2>
        <p>You will need an invitation to rejoin.</p>
      </div>
    </ModalComponent>

    <ModalComponent
      v-if="modal.groupUsers"
      title="All group attendees"
      on-close-text="CLOSE"
      @close="groupUsersClose"
    >
      <div class="modal-group-users">
        <div class="mb--20" v-if="chatUsers('moderator')">
          <strong>Moderator</strong>
          <div>{{ chatUsers('moderator') }}</div>
        </div>
        <div class="mb--20" v-if="chatUsers('joined')">
          <strong>Joined</strong>
          <div>{{ chatUsers('joined') }}</div>
        </div>
        <div class="mb--20" v-if="chatUsers('pending')">
          <strong>Pending</strong>
          <div>{{ chatUsers('pending') }}</div>
        </div>
        <div class="mb--20" v-if="chatUsers('declined')">
          <strong>Declined</strong>
          <div>{{ chatUsers('declined') }}</div>
        </div>
      </div>
    </ModalComponent>
    <GroupNameModal
      v-if="showGroupNameModal"
      :current-group-name="group.name"
      @confirm="onGroupNameConfirm"
      @cancel="onGroupNameCancel"
    />
  </div>
</template>

<style lang="scss" scoped>
.modal__content--no-header {
  display: flex;
  flex-direction: column;

  button {
    flex: 1;
    width: fit-content;
  }
}

.modal__content__remove__users {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  padding: 24px 24px 12px 24px;

  h2 {
    margin: 0;
  }

  &__list {
    display: flex;
    flex-direction: column;
    row-gap: 6px;

    > div {
      display: flex;
      column-gap: 8px;

      img {
        width: 24px;
        height: 24px;
        object-fit: cover;
        border: 0.5px solid #d3d3d3;
        border-radius: 50%;
      }

      span:not(.user__name) {
        display: flex;
        width: 24px;
        height: 24px;
        border: 0.5px solid #d3d3d3;
        border-radius: 50%;

        svg {
          width: 100%;
          height: 100%;
        }
      }

      .user__name {
        flex: 1;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        color: #4b444d;
      }
    }
  }
}

.modal__content__leave__chat {
  display: flex;
  flex-direction: column;
  row-gap: 6px;
  padding: 24px 24px 12px 24px;

  h2 {
    margin: 0;
  }
}

.modal__custom-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

:deep(.xl-emoji-picker__wrapper) {
  flex: 2;
  width: 100%;
  background: #F5F5F5;
  overflow-y: auto;
  box-shadow: inset 0 -40px 30px -30px #00000026;

  .xl-emoji-picker {
    .v3-body-inner {
      display: flex;
      flex-direction: column;
      row-gap: 12px;
  
      .v3-emojis {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        justify-items: center;
        row-gap: 48px;
  
        button {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 24px;
          height: 24px;
          padding: 0 !important;
          scale: 2;
          background: transparent;
          border: none;
          border-radius: 8px;
          cursor: pointer;

          &:hover {
            background: #E3E3E3;
          }
        }
      }
  
      .v3-group {
        display: flex;
        flex-direction: column;
        row-gap: 12px;
  
        h5 {
          position: sticky;
          top: 0;
          background: #F5F5F5;
          margin: 0 !important;
          text-align: center;
          z-index: 10;
        }
      }
    }
  
    .v3-footer {
      display: none !important;
    }
  }
}

.emoji-slide-enter-active,
.emoji-slide-leave-active {
  transition: all 0.3s ease-in-out;
  overflow: hidden;
}

.emoji-slide-enter-from,
.emoji-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.emoji-slide-enter-to,
.emoji-slide-leave-from {
  max-height: 300px;
  opacity: 1;
}

/* Replaces the composer when in-session chat is disabled: history stays
   readable, but there is nothing to type into. */
.form__disabled-notice {
  padding: 14px 16px;
  text-align: center;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  line-height: 1.4;
  color: #4b444d;
  background: #f4f2f5;
  border-radius: 8px;
}

/* The reactions and the time now sit inside the bubble's own column (so the
   design can put the name under the bubble). Keep my own bubble on the right
   when the time under it is wider than the message. */
.message__container__text__container--text--message.you .bubble {
  align-self: flex-end;
}

/* ===================================================================
   Northstar: the in-session panel's chat (prototype §2, "rmchat").
   Applies only under the panel's [data-xl-gen="northstar"] root; the
   hub-wide chat outside it keeps the Generation 1 look from chat.scss.
   chat.scss nests these selectors five and six classes deep, so the root
   class is repeated to outrank it without !important.
   =================================================================== */
$ns: '[data-xl-gen="northstar"] .xl-rm.xl-rm.xl-rm.xl-rm';

$primary: var(--xl-color-primary, #5E2F78);
$tint-2: var(--xl-color-surface-tint, #8154A6);
$tint: var(--xl-color-primary-container, #F5EFF9);
$tint-3: #D9C6E8;
$line: var(--xl-color-border, #EDE7F0);
$line-2: var(--xl-color-border-subtle, #F6F2F8);
$ink: var(--xl-color-on-surface, #1C1720);
$ink-2: var(--xl-color-on-surface-variant, #514A57);
$mut: var(--xl-color-outline, #867D8D);
$faint: var(--xl-color-outline-variant, #B6AEBC);
$cr: var(--xl-color-error, #A32020);
$cr-bg: var(--xl-color-error-container, #FCEBEB);
$paper: var(--xl-color-surface, #FCFAFD);
$font: var(--xl-font-family, 'General Sans', Inter, -apple-system, 'Segoe UI', sans-serif);
$grad: linear-gradient(155deg, $tint-2, $primary);
$sh: 0 1px 2px rgba(28, 23, 32, 0.04);

#{$ns} {
  font-family: $font;
  letter-spacing: -0.006em;
  color: $ink;
  background: #fff;

  textarea,
  button {
    font-family: $font;
  }

  /* ---- Conversation header: back, name, actions (the room has no
     conversation header, so it borrows the panel header's .rmhide buttons) */
  .messages__header {
    gap: 6px;
    padding: 9px 10px;
    border-bottom: 1px solid $line;
    background: #fff;
  }

  .messages__profiles {
    column-gap: 6px;
  }

  .messages__profiles--btn,
  .messages__actions__btn,
  :deep(.dropdown__btn) {
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 9px;
    color: $mut;
    transition: background-color 0.15s, color 0.15s;

    svg {
      width: 18px;
      height: 18px;
    }

    svg path {
      fill: currentColor;
    }

    &:hover {
      background-color: $line-2;
      color: $ink;
    }

    &:focus-visible {
      outline: 2px solid $tint-2;
      outline-offset: 1px;
    }
  }

  .messages__profiles--names {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.01em;
    color: $ink;
  }

  .messages__actions {
    column-gap: 4px;
  }

  :deep(.dropdown) {
    margin: 0;
  }

  /* The conversation's menu: the room's floating bar (.rxbar) language. */
  .messages__header :deep(.dropdown ul) {
    top: calc(100% + 6px);
    min-width: 200px;
    padding: 5px;
    background: #fff;
    border: 1px solid $line;
    border-radius: 14px;
    box-shadow: 0 14px 34px rgba(28, 23, 32, 0.18);

    li {
      height: 36px;
      padding: 0 10px;
      border-radius: 9px;
      column-gap: 10px;
      font-size: 13px;
      font-weight: 500;
      line-height: 1;
      color: $ink-2;

      svg {
        width: 18px;
        height: 18px;
      }

      &:hover {
        background-color: $line-2;
      }

      &.dropdown-delete {
        color: $cr;

        &:hover {
          background-color: $cr-bg;
        }
      }
    }
  }

  /* ---- The thread (.rmchat) */
  .messages__block__content__list {
    padding: 14px 0;
    background: #fff;

    > li {
      margin-bottom: 0;
    }

    ul {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 0 14px !important;
      margin-bottom: 12px;

      li {
        margin-bottom: 0;
      }
    }
  }

  /* No timestamps in the design; the app keeps its day markers, drawn as a
     quiet chip rather than a floating shadowed pill. */
  .messages__date {
    top: 0;
    height: auto;
    margin: 0 0 12px;
    padding: 2px 0;

    span {
      padding: 3px 10px;
      border: 1px solid $line;
      border-radius: 99px;
      background: #fff;
      box-shadow: none;
      font-size: 11px;
      font-weight: 600;
      line-height: 16px;
      color: $mut;
      text-transform: capitalize;
    }
  }

  /* The face belongs to the person, so it sits on the person's line — the
     name and time above the bubble — not beside the end of what they said
     (prototype cd192; In-session chat → "Update how a message is displayed"). */
  .message__container {
    align-items: flex-start;
    column-gap: 9px;
  }

  /* .face at 26px; my own messages carry no avatar */
  .message__container--avatar {
    display: grid;
    place-items: center;
    flex: 0 0 26px;
    width: 26px;
    height: 26px;
    align-self: flex-start;
    border-radius: 50%;
    overflow: hidden;
    background: $grad;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    svg {
      width: 18px;
      height: 18px;
      margin-top: 4px;

      path {
        fill: #fff;
      }
    }
  }

  .message__container__text__container {
    row-gap: 0;
    min-width: 0;
  }

  .message__container__text__container--text {
    align-items: flex-start;
    column-gap: 4px;
  }

  /* No wider than about three quarters of the panel (the prototype's 78%), so a
     long message does not run edge to edge. */
  .message__container__text__container--text--message {
    max-width: 78%;
  }

  /* The name and the time on one line, the bubble under them, its reactions
     under that: "Maya Lewis · 2:02 PM". */
  .message__container__text__container--text--message .bubble-container {
    display: grid;
    grid-template-columns: minmax(0, auto) auto 1fr;
    column-gap: 0;
    row-gap: 0;
    min-width: 0;

    > strong {
      grid-row: 1;
      grid-column: 1;
      align-self: center;
      height: 26px;
      margin-bottom: 2px;
      font-size: 11.5px;
      font-weight: 500;
      line-height: 26px;
      color: $ink-2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .bubble {
      grid-row: 2;
      grid-column: 1 / -1;
      justify-self: start;
      max-width: 100%;
      padding: 9px 13px;
      background: #fff;
      border: 1px solid $line;
      border-radius: 4px 14px 14px 14px;

      div:last-child {
        font-size: 13.5px;
        line-height: 1.5;
        color: $ink;
      }
    }

    .message__container__text__container--reactions {
      grid-row: 3;
      grid-column: 1 / -1;
    }

    /* Quieter than the name: smaller, lighter, never italic. */
    .message__container__text__container--time {
      grid-row: 1;
      grid-column: 2;
      align-self: center;
      height: 26px;
      margin-bottom: 2px;
      white-space: nowrap;

      span {
        font-size: 10.5px;
        font-style: normal;
        font-weight: 500;
        line-height: 26px;
        color: $faint;

        &::before {
          content: '\00a0·\00a0';
        }
      }
    }
  }

  .message__container__text__container--text--message.you .bubble-container {
    .bubble {
      justify-self: end;
      background: $grad;
      border-color: transparent;
      border-radius: 14px 4px 14px 14px !important;

      div:last-child {
        color: #fff;
      }
    }

    /* My own: the name and time above, to the right. The hub-wide chat hides
       my name on my own messages (chat.scss); the session's shows it. */
    grid-template-columns: 1fr minmax(0, auto) auto;

    > strong {
      display: block;
      grid-column: 2;
    }

    .message__container__text__container--time {
      grid-column: 3;
    }
  }

  .message__container__text__container--text--message.you {
    margin-left: auto;
  }

  /* The hover React button (.rxbtn): 24px, faint, tinted on hover. */
  .message__container__text__container--text :deep(.pge-button-container.rounded-full) {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;

    .pge-button {
      width: 24px;
      height: 24px;
      margin-top: 8px !important;
      border-radius: 50%;
      color: $faint;

      &:hover {
        background-color: $tint !important;
        color: $primary;
      }
    }

    .pge-button-content.rounded-full {
      width: 24px;
      height: 24px;
      padding: 0 !important;
    }

    svg {
      width: 15px;
      height: 15px;
    }

    svg path {
      fill: currentColor;
    }
  }

  /* ---- Reactions (.rxrow / .rxc) */
  .message__container__text__container--reactions {
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    min-height: 22px;

    .reaction-badge {
      gap: 4px;
      height: 24px;
      padding: 0 8px;
      border-radius: 99px;
      background-color: #fff;
      border: 1px solid $line;
      box-shadow: $sh;
      font-size: 13px;
      line-height: 1;
      transition: border-color 0.12s, background-color 0.12s;

      &:hover {
        background-color: #fff;
        border-color: $tint-3;
      }

      &.user-reacted {
        background-color: $tint;
        border-color: $tint-3;

        .reaction-badge--count {
          color: $primary;
        }
      }
    }

    .reaction-badge--count {
      margin-left: 0;
      font-size: 11.5px;
      font-weight: 600;
      line-height: 1;
      color: $mut;
    }
  }

  /* ---- Composer (.rmsend): emoji, a pill input, the round send */
  .form {
    padding: 11px 14px;
    border-top: 1px solid $line;
    background: #fff;
  }

  .form__message,
  .form__message.focused {
    gap: 8px;
    align-items: flex-end;
    min-height: 0;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 0;

    textarea {
      flex: 1;
      min-width: 0;
      height: 36px;
      min-height: 36px;
      margin: 1px 0;
      padding: 7px 14px;
      border: 1px solid $line;
      border-radius: 18px;
      background: #fff;
      font-size: 13px;
      line-height: 20px;
      color: $ink;
      scrollbar-width: none;
      transition: border-color 0.15s, box-shadow 0.15s;

      &::-webkit-scrollbar {
        display: none;
      }

      &::placeholder {
        color: $faint;
      }

      &:focus {
        border-color: $primary;
        box-shadow: 0 0 0 3px rgba(94, 47, 120, 0.1);
      }
    }
  }

  /* .rnd.emo: 36 wide, 38 tall, white with a hairline */
  .form__message :deep(.form__emoji.pge-button-container) {
    width: 36px;
    height: 38px;
    flex: 0 0 36px;

    .pge-button {
      width: 36px;
      height: 38px;
      border-radius: 50%;
      background-color: #fff !important;
      border: 1px solid $line;
      color: $mut;
      transition: border-color 0.15s, color 0.15s, background-color 0.15s;

      &:hover {
        border-color: $tint-2;
        color: $primary;
      }
    }

    .pge-button-content.rounded-full {
      width: 100%;
      height: 100%;
      padding: 0 !important;
    }

    svg {
      width: 19px;
      height: 19px;
      fill: currentColor;
    }
  }

  .form__message :deep(.form__emoji.is-on .pge-button) {
    background-color: $tint !important;
    border-color: $tint-3;
    color: $primary;
  }

  /* .rnd.p: the round gradient send */
  .form__message :deep(.form__send-btn.pge-button-container) {
    width: 38px;
    height: 38px;
    flex: 0 0 38px;

    .pge-button {
      width: 38px;
      height: 38px;
      border-radius: 50%;
      background-image: $grad;
      color: #fff;

      &:hover {
        box-shadow: 0 4px 12px rgba(94, 47, 120, 0.28);
      }
    }

    .pge-button-content.rounded-full {
      width: 100%;
      height: 100%;
      padding: 0 !important;
    }

    svg {
      width: 17px;
      height: 17px;
      margin-left: 2px;
    }

    svg path {
      fill: #fff;
    }
  }

  .form__message :deep(.pge-button:focus-visible) {
    outline: 2px solid $tint-2;
    outline-offset: 2px;
  }

  .form__disabled-notice {
    width: 100%;
    padding: 10px 14px;
    border-radius: 12px;
    background: $line-2;
    font-family: $font;
    font-size: 12.5px;
    line-height: 1.55;
    color: $mut;
  }
}

@media (prefers-reduced-motion: reduce) {
  #{$ns} {
    .messages__profiles--btn,
    .messages__actions__btn,
    .reaction-badge,
    .form__message textarea,
    :deep(.pge-button) {
      transition: none;
    }
  }
}

/* ---------- The session's chat on a phone (prototype sheetChat) ---------- */
/* A red "New" line where the unread start (prototype .newmark). */
.newmark {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 10px;
  color: #E0473F;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.newmark::before, .newmark::after { content: ""; flex: 1; height: 1px; background: #F3C2BE; }

/* prototype .empty */
.chat-empty { padding: 30px 18px 26px; color: #867D8D; text-align: center; list-style: none; }
.chat-empty b { display: block; margin-bottom: 4px; color: #1C1720; font-size: 15px; }
.chat-empty p { max-width: 250px; margin: 0 auto; font-size: 13px; line-height: 1.5; }

.host-mark { color: #867D8D; font-weight: 500; }

/* Inside the room's namespace, which carries its weight on purpose. */
#{$ns} {
  /* Everybody else's on a soft lilac, as the board draws it (prototype .msg .b). */
  .messages__block__content.is-phone-chat .message__container__text__container--text--message:not(.you) .bubble-container .bubble {
    border-color: transparent;
    background: #F2EEF5;
  }

  /* At least 16px, or an iPhone zooms the page when the box is tapped. */
  .messages__block__content.is-phone-chat .form .form__message textarea { font-size: 16px; }
}
</style>
